import {
  type KnownUser,
  type ErrorResult,
  type UserReference,
} from '@databases/types/confluence';
import { getJiraIssue } from '@databases/utils/confluence/jira';
import { searchUsers } from '@databases/utils/confluence/user';
import DataLoader from 'dataloader';

export class DataLoaderService {
  private userLoader: DataLoader<string, UserReference | ErrorResult>;
  private jiraIssueLoader: DataLoader<string, string | ErrorResult>;
  private jiraBaseUrl?: URL; // TODO: Create abstract class for each field type data loader

  constructor() {
    this.userLoader = new DataLoader(this.batchLoadUsers.bind(this), {
      batchScheduleFn: (callback) => setTimeout(callback, 1000),
    });
    this.jiraIssueLoader = new DataLoader(this.batchLoadJiraIssues.bind(this), {
      batchScheduleFn: (callback) => setTimeout(callback, 1000),
    });
  }

  loadUsers(usernames: string[]): Promise<(UserReference | ErrorResult)[]> {
    return Promise.all(usernames.map((username) => this.userLoader.load(username)));
  }

  loadJiraIssues(issueKeys: string[]): Promise<(string | ErrorResult)[]> {
    return Promise.all(issueKeys.map((key) => this.jiraIssueLoader.load(key)));
  }

  clearCache() {
    this.userLoader.clearAll();
    this.jiraIssueLoader.clearAll();
  }

  public getJiraBaseUrl(): URL | undefined {
    return this.jiraBaseUrl;
  }

  public setJiraBaseUrl(baseUrl: URL): void {
    this.jiraBaseUrl = baseUrl;
  }

  private batchLoadUsers(
    usernames: readonly string[]
  ): Promise<(UserReference | ErrorResult)[]> {
    return Promise.all(
      Array.from(usernames).map(async (username) => {
        try {
          const cql = `user.fullname~"${username}"`;
          const userResult = searchUsers(cql, [], 1);
          for await (const result of userResult) {
            if ('user' in result) {
              const user = result.user as KnownUser;
              if (user.accountId) {
                return { accountId: user.accountId, displayName: user.displayName };
              }
            }
          }
          return { message: `User not found: ${username}`, status: 404 };
        } catch (error) {
          const errorResult = error as ErrorResult;
          return {
            message: `Search error for ${username}: ${errorResult.message}`,
            status: errorResult.status,
          };
        }
      })
    );
  }

  private batchLoadJiraIssues(
    issueKeys: readonly string[]
  ): Promise<(string | ErrorResult)[]> {
    if (!this.jiraBaseUrl) {
      const error: ErrorResult = {
        message: 'Jira base URL is not configured',
        status: 500,
      };
      return Promise.resolve(issueKeys.map(() => error));
    }

    return Promise.all(
      Array.from(issueKeys).map(async (issueKey) => {
        try {
          const jiraIssueResult =
            this.jiraBaseUrl && (await getJiraIssue(this.jiraBaseUrl, issueKey));
          if ('key' in jiraIssueResult) {
            return jiraIssueResult.key;
          }
          return {
            message: `Key not in response for ${issueKey}`,
            status: 500,
          } as ErrorResult;
        } catch (error) {
          const errorResult = error as ErrorResult;
          return {
            message: `Search error for ${issueKey}: ${errorResult.message}`,
            status: errorResult.status,
          };
        }
      })
    );
  }
}
